import { useState, useEffect, useCallback, useRef } from 'react';
import i18n from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import type { Message } from '@/types/message-types';
import { useAuthRecovery } from './useAuthRecovery';

interface ConversationData {
  id: string;
  status: string;
  created_at: string;
  last_message_at: string | null;
}

interface CustomerData {
  id: string;
  name: string;
  phone_number: string;
  external_id: string;
}

interface UseTenantConversationMessagesReturn {
  messages: Message[];
  conversation: ConversationData | null;
  customer: CustomerData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  loadMoreMessages: () => Promise<void>;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
}

export function useTenantConversationMessages(
  tenantId: string,
  conversationId: string,
  tenantSchema?: string,
  customerId?: string
): UseTenantConversationMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [lastMessageTime, setLastMessageTime] = useState<string | null>(null);

  // [FIX: MESSAGE-MIXING] AbortController to cancel in-flight requests when conversation changes
  const abortControllerRef = useRef<AbortController | null>(null);

  // Add authentication recovery system
  const { handleError: handleAuthError, resetRecovery } = useAuthRecovery({
    onRecoverySuccess: () => {
      // Retry fetching data after successful recovery
      if (tenantId && conversationId) {
        fetchData(conversationId);
      }
    }
  });

  const fetchData = async (
    targetConversationId: string,
    createdBefore?: string,
    isLoadMore = false,
    cursorId?: string,
    silent = false
  ) => {
    const perfLabel = `⏱️ TOTAL fetchData (${targetConversationId.slice(0, 8)})`;
    console.time(perfLabel);

    // [FIX: MESSAGE-MIXING] Cancel any previous fetch for old conversation
    if (abortControllerRef.current) {
      console.log('⚠️ [FIX: MESSAGE-MIXING] Aborting previous fetch');
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this fetch
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      if (!isLoadMore && !silent) {
        setLoading(true);
      } else if (isLoadMore) {
        setIsLoadingMore(true);
      }
      setError(null);

      console.log('🔄 [FIX: MESSAGE-MIXING] fetchData called:', {
        targetConversationId,
        currentParamConversationId: conversationId,
        isLoadMore,
        createdBefore,
        currentLastMessageTime: lastMessageTime
      });

      // Get tenant schema - use passed schema or query as fallback
      let schemaName: string;
      if (tenantSchema) {
        // Schema provided - use it directly (0ms)
        schemaName = tenantSchema;
        console.log('✅ Using provided tenant schema:', schemaName);
      } else {
        // Backward compatibility - query if not provided
        console.time('⏱️ Query 1: Get tenant schema (fallback)');
        const { data: tenant, error: tenantError } = await supabase
          .from('tenants')
          .select('schema_name')
          .eq('id', tenantId)
          .single();
        console.timeEnd('⏱️ Query 1: Get tenant schema (fallback)');

        if (tenantError || !tenant?.schema_name) {
          throw new Error('Tenant not found or invalid');
        }
        schemaName = tenant.schema_name;
      }

      let messagesResult: unknown;
      let metadataResult: unknown;

      if (customerId) {
        // Customer mode: fetch ALL messages across ALL conversations for this customer
        console.time('⏱️ Query 2: Get customer timeline messages via RPC');
        messagesResult = await supabase
          .rpc('get_tenant_customer_messages_timeline', {
            p_tenant_schema: schemaName,
            p_customer_id: customerId,
            p_limit: 50,
            p_cursor_created_at: isLoadMore ? (createdBefore || null) : null,
            p_cursor_id: isLoadMore ? (cursorId || null) : null
          })
          .abortSignal(abortController.signal);
        console.timeEnd('⏱️ Query 2: Get customer timeline messages via RPC');
        metadataResult = { data: null, error: null };
      } else {
        // Single conversation mode (existing behavior)
        console.time('⏱️ Query 2: Get messages via RPC');
        console.time('⏱️ Query 3: Get conversation metadata');

        [messagesResult, metadataResult] = await Promise.all([
          supabase
            .rpc('get_tenant_conversation_messages_with_status', {
              tenant_schema: schemaName,
              conversation_id_param: targetConversationId,
              limit_count: 50,
              created_before: createdBefore
            })
            .abortSignal(abortController.signal),

          supabase
            .rpc('get_tenant_conversation_by_id', {
              p_tenant_schema: schemaName,
              p_conversation_id: targetConversationId
            })
            .abortSignal(abortController.signal)
        ]);

        console.timeEnd('⏱️ Query 2: Get messages via RPC');
        console.timeEnd('⏱️ Query 3: Get conversation metadata');
      }

      const { data: conversationData, error: conversationError } = messagesResult;
      const { data: convData, error: convError } = metadataResult;

      if (conversationError) {
        throw new Error(conversationError.message);
      }

      // Handle empty results case - now uses metadata from Promise.all
      if (!conversationData || conversationData.length === 0) {
        if (isLoadMore) {
          setHasMoreMessages(false);
        } else {
          // Process metadata (already fetched in parallel above)
          if (!convError && convData && convData.length > 0) {
            const conv = convData[0];
            setConversation({
              id: conv.id,
              status: conv.status,
              created_at: conv.created_at,
              last_message_at: conv.last_message_at
            });

            if (conv.last_message_at) {
              setLastMessageTime(conv.last_message_at);
              console.log('📝 Updated lastMessageTime from conversation (no messages):', conv.last_message_at);
            }

            setCustomer({
              id: conv.customer_id,
              name: conv.customer_name,
              phone_number: conv.customer_phone,
              external_id: conv.customer_whatsapp_id
            });
          }

          setMessages([]);
        }
        return;
      }

      // Process messages and extract conversation/customer info
      const processedMessages: Message[] = conversationData.map((row: Record<string, unknown>) => ({
        id: row.id,
        conversation_id: row.conversation_id,
        sender_type: row.sender_type,
        sender_id: row.sender_id || null,
        sender_uuid: row.sender_uuid || null,
        content: row.content,
        message_type: row.message_type || 'text',
        external_id: row.external_id,
        media_url: row.media_url,
        metadata: row.metadata || null,
        ai_model: row.ai_model || null,
        status: row.status,
        read_at: row.read_at,
        created_at: row.created_at,
        updated_at: row.updated_at,
        is_read: row.is_read || false
      }));

      // DEBUG: Log messages with reply context
      const messagesWithReplies = processedMessages.filter(m => m.metadata?.context);
      if (messagesWithReplies.length > 0) {
        console.log('🔍 DEBUG: Messages with reply context from RPC:', {
          count: messagesWithReplies.length,
          messages: messagesWithReplies.map(m => ({
            id: m.id.substring(0, 8),
            content: m.content.substring(0, 20),
            hasMetadata: !!m.metadata,
            hasContext: !!m.metadata?.context,
            contextId: m.metadata?.context?.id?.substring(0, 20) + '...'
          }))
        });
      }

      // Database returns DESC order (newest first), reverse for chat display (oldest first)
      const reversedMessages = processedMessages.reverse();

      // Check if we have fewer messages than requested (means no more data)
      if (conversationData.length < 50) {
        setHasMoreMessages(false);
      }

      console.log('🔍 useTenantConversationMessages: fetchData results', {
        totalMessages: reversedMessages.length,
        aiMessages: reversedMessages.filter(m => m.sender_type === 'ai_agent_automatic').length,
        isLoadMore,
        conversationId,
        latestMessages: reversedMessages.slice(-3).map(m => ({
          id: m.id,
          sender_type: m.sender_type,
          content: m.content?.substring(0, 30)
        }))
      });

      if (isLoadMore) {
        // Prepend older messages to existing messages
        setMessages(prev => {
          const newMessages = [...reversedMessages, ...prev];
          console.log('📊 useTenantConversationMessages: loadMore updated state', {
            newCount: newMessages.length,
            aiMessages: newMessages.filter(m => m.sender_type === 'ai_agent_automatic').length
          });
          return newMessages;
        });
      } else {
        setMessages(reversedMessages);

        console.log('📊 useTenantConversationMessages: full refresh updated state', {
          newCount: reversedMessages.length,
          aiMessages: reversedMessages.filter(m => m.sender_type === 'ai_agent_automatic').length
        });

        // CRITICAL: Always update last message time when we refresh
        if (reversedMessages.length > 0) {
          const newestMessage = reversedMessages[reversedMessages.length - 1];
          const newLastMessageTime = newestMessage.created_at;
          setLastMessageTime(newLastMessageTime);
          console.log('📝 Updated lastMessageTime from newest message:', {
            messageId: newestMessage.id,
            timestamp: newLastMessageTime,
            content: newestMessage.content.substring(0, 50)
          });
        }

        // Process metadata (already fetched in parallel above)
        if (!convError && convData && convData.length > 0) {
          const conv = convData[0];
          setConversation({
            id: conv.id,
            status: conv.status,
            created_at: conv.created_at,
            last_message_at: conv.last_message_at
          });

          // Also update from conversation data if it's newer than what we have
          if (conv.last_message_at) {
            const convTime = new Date(conv.last_message_at);
            const currentTime = lastMessageTime ? new Date(lastMessageTime) : new Date(0);

            if (convTime > currentTime) {
              setLastMessageTime(conv.last_message_at);
              console.log('📝 Updated lastMessageTime from conversation (newer):', conv.last_message_at);
            }
          }

          setCustomer({
            id: conv.customer_id,
            name: conv.customer_name,
            phone_number: conv.customer_phone,
            external_id: conv.customer_whatsapp_id
          });
        }
      }

    } catch (err) {
      // [FIX: MESSAGE-MIXING] Ignore abort errors (expected when switching conversations)
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('✅ [FIX: MESSAGE-MIXING] Fetch aborted (conversation switched)', {
          targetConversationId,
          currentConversationId: conversationId
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : i18n.t('conversations:hookErrors.fetchMessagesFailed');
      console.error('Error fetching conversation messages:', err);
      
      // Attempt authentication recovery for auth-related errors
      const recovered = await handleAuthError(errorMessage, {
        tenantId,
        conversationId,
        isLoadMore,
        createdBefore
      });
      
      // Only set error if recovery wasn't attempted or failed
      if (!recovered) {
        setError(errorMessage);
      }
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
      console.timeEnd(perfLabel);
    }
  };

  const loadMoreMessages = async () => {
    if (isLoadingMore || !hasMoreMessages || messages.length === 0) return;

    // Get the oldest message timestamp and ID for cursor pagination
    const oldestMessage = messages[0];
    if (oldestMessage) {
      await fetchData(conversationId, oldestMessage.created_at, true, oldestMessage.id);
    }
  };

  useEffect(() => {
    if (tenantId && (customerId || conversationId)) {
      // Reset recovery state when starting fresh
      resetRecovery();
      setMessages([]);
      setHasMoreMessages(true);
      setLastMessageTime(null);

      // [FIX: MESSAGE-MIXING] Cancel any in-flight fetch from previous conversation/customer
      if (abortControllerRef.current) {
        console.log('🛑 [FIX: MESSAGE-MIXING] Aborting fetch on conversation/customer change');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      fetchData(conversationId);
    }
  }, [tenantId, conversationId, customerId, resetRecovery]);

  // REDUCED backup polling - now every 30 seconds when unhealthy, 5 minutes when healthy
  useEffect(() => {
    if (!tenantId || (!conversationId && !customerId)) return;

    console.log('🕐 Setting up reduced backup polling for conversation:', {
      tenantId,
      conversationId,
      lastMessageTime,
      hasConversation: !!conversation
    });

    const getPollingInterval = () => {
      // If we have a recent message (within last 2 minutes), use longer interval
      if (lastMessageTime) {
        const timeSinceLastMessage = Date.now() - new Date(lastMessageTime).getTime();
        if (timeSinceLastMessage < 120000) { // 2 minutes
          return 300000; // 5 minutes - real-time is likely working
        }
      }
      return 30000; // 30 seconds - more conservative than before
    };

    const pollInterval = setInterval(async () => {
      try {
        // Customer mode: simple periodic refetch (single-conv metadata can't detect cross-conv messages)
        if (customerId) {
          if (import.meta.env.DEV) console.log('📡 BACKUP POLLING (customer mode): Periodic refetch');
          fetchData(conversationId, undefined, false, undefined, true);
          return;
        }

        // Get tenant schema - use passed schema or query as fallback
        let pollingSchemaName: string;
        if (tenantSchema) {
          pollingSchemaName = tenantSchema;
        } else {
          const { data: tenant, error: tenantError } = await supabase
            .from('tenants')
            .select('schema_name')
            .eq('id', tenantId)
            .single();

          if (tenantError || !tenant?.schema_name) return;
          pollingSchemaName = tenant.schema_name;
        }

        // Check if conversation has newer messages using optimized single-conversation RPC
        const { data: convData, error: convError } = await supabase
          .rpc('get_tenant_conversation_by_id', {
            p_tenant_schema: pollingSchemaName,
            p_conversation_id: conversationId
          });

        if (!convError && convData && convData.length > 0) {
          const conv = convData[0];
          console.log('📡 BACKUP POLLING: Conversation check result:', {
            foundConversation: !!conv,
            conversationId,
            convLastMessageAt: conv?.last_message_at,
            ourLastMessageTime: lastMessageTime,
            totalConversations: convData.length,
            checkingForAIMessage: conv?.last_message_at > '2025-08-31T19:00:00Z' ? 'RECENT' : 'OLD',
            aiMessageExists: conv?.last_message_at < '2025-08-31T19:01:11Z' ? 'AI_MSG_NEWER' : 'CONV_UPDATED'
          });

          if (conv && conv.last_message_at) {
            // WORKAROUND: Check for AI messages directly since conversation.last_message_at isn't updated
            // Get the actual latest message from messages table
            const { data: latestMessages, error: latestError } = await supabase
              .rpc('get_tenant_conversation_messages_with_status', {
                tenant_schema: pollingSchemaName,
                conversation_id_param: conversationId,
                limit_count: 1
              });

            let actualLatestTime = conv.last_message_at;
            if (!latestError && latestMessages && latestMessages.length > 0) {
              actualLatestTime = latestMessages[0].created_at;
              console.log('📡 BACKUP POLLING: Found actual latest message:', {
                messageId: latestMessages[0].id,
                senderType: latestMessages[0].sender_type,
                actualTime: actualLatestTime,
                conversationTime: conv.last_message_at,
                isNewer: actualLatestTime > conv.last_message_at
              });
            }

            // If we don't have a lastMessageTime yet, or there are newer messages than we have
            if (!lastMessageTime || (lastMessageTime && actualLatestTime > lastMessageTime)) {
              console.log('📡 BACKUP POLLING: Detected new messages, refreshing...', {
                conversationLastMessage: conv.last_message_at,
                actualLatestMessage: actualLatestTime,
                ourLastMessage: lastMessageTime,
                conversationId,
                reason: !lastMessageTime ? 'no_last_time' : 'newer_messages',
                usingActualTime: actualLatestTime !== conv.last_message_at
              });
              fetchData(conversationId, undefined, false, undefined, true);

              // After successful fetch, extend the polling interval
              if (pollInterval) {
                clearInterval(pollInterval);
                const newInterval = setInterval(arguments.callee as () => void, 300000); // 5 minutes
                console.log('📡 BACKUP POLLING: Extended interval to 5 minutes after successful fetch');
              }
            } else {
              console.log('📡 BACKUP POLLING: No new messages detected', {
                conversationLastMessage: conv.last_message_at,
                actualLatestMessage: actualLatestTime,
                ourLastMessage: lastMessageTime,
                timeDifference: new Date(actualLatestTime).getTime() - new Date(lastMessageTime).getTime(),
                bothTimestamps: {
                  conversation: conv.last_message_at,
                  actual: actualLatestTime,
                  ourLast: lastMessageTime
                }
              });
            }
          }
        }
      } catch (error) {
        console.log('📡 Backup polling error (non-critical):', error);
      }
    }, getPollingInterval()); // Dynamic interval based on activity

    return () => clearInterval(pollInterval);
  }, [tenantId, conversationId, customerId, lastMessageTime]);

  const refetch = useCallback(() => {
    console.log('🔄 [FIX: MESSAGE-MIXING] useTenantConversationMessages: refetch called', {
      conversationId
      // Removed message counts - caused callback instability
    });
    return fetchData(conversationId);
  }, [conversationId]);

  return {
    messages,
    conversation,
    customer,
    loading,
    error,
    refetch,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore
  };
}